import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  activeTabKeys = {};
  constructor(private router: Router) {}

  getFromSession(key: string) {
    return sessionStorage.getItem(key);
  }

  getObjectFromSession(key: string) {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  setToSession(key: string, value: string) {
    sessionStorage.setItem(key, value);
  }

  setObjectToSession(key: string, value: any) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getActiveTabIndex(tabName: string) {
    // dashboardActiveIndex
    return parseInt(sessionStorage.getItem(tabName) || '0');
  }

  setActiveTab(index: number, tabName: string) {
    sessionStorage.setItem(tabName, index.toString());
  }

  openAsset(id: number) {
    this.router.navigate(['/asset-details', { id: id }]);
  }

  openSite(id: number) {
    this.router.navigate(['/site-details', { id: id }]);
  }

  deepEqual(obj1: any, obj2: any) {
    // Base case: if both objects are strictly equal, return true
    if (obj1 === obj2) {
      return true;
    }

    // Check if both objects are objects
    if (
      typeof obj1 !== 'object' ||
      typeof obj2 !== 'object' ||
      obj1 === null ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Check if both objects have the same number of keys
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Check if all keys in obj1 exist in obj2 and have deep equality
    for (const key of keys1) {
      if (!keys2.includes(key) || !this.deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    // If all checks pass, the objects are considered equal
    return true;
  }

  deepCompare(obj1: any, obj2: any): string[] | null {
    const differingKeys: string[] = [];

    function compare(o1: any, o2: any, path: string[] = []) {
      if (o1 === o2) return;

      if (
        typeof o1 !== 'object' ||
        typeof o2 !== 'object' ||
        o1 === null ||
        o2 === null
      ) {
        differingKeys.push(path.join('.'));
        return;
      }

      const allKeys = new Set([...Object.keys(o1), ...Object.keys(o2)]);
      allKeys.forEach((key) => {
        compare(o1[key], o2[key], path.concat(key));
      });
    }

    compare(obj1, obj2);
    return differingKeys.length > 0 ? differingKeys : null;
  }

  deepCompareTopLevel(obj1: any, obj2: any): string[] | null {
    const differingKeys: Set<string> = new Set();

    function compare(o1: any, o2: any, key: string) {
      if (o1 === o2) return;

      if (
        typeof o1 !== 'object' ||
        typeof o2 !== 'object' ||
        o1 === null ||
        o2 === null
      ) {
        differingKeys.add(key);
        return;
      }

      const allKeys = new Set([...Object.keys(o1), ...Object.keys(o2)]);
      allKeys.forEach((k) => {
        compare(o1[k], o2[k], key);
      });
    }

    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    allKeys.forEach((key) => {
      compare(obj1[key], obj2[key], key);
    });

    return differingKeys.size > 0 ? Array.from(differingKeys) : null;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const utcDateString = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateString);
    if (date.getFullYear() < 1980 || !date.valueOf()) return 'N/A';
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  }

  formatDateWithTime(dateStr: string): string {
    if (!dateStr) return '';
    const utcDateString = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z';
    const date = new Date(utcDateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  isDateRangeValid(startDate: string, endDate: string) {
    // check if it a valid date
    const startDateVal = new Date(startDate);
    const endDateVal = new Date(endDate);
    // check if start date is less than end date
    return (
      !isNaN(startDateVal.valueOf()) &&
      !isNaN(endDateVal.valueOf()) &&
      startDateVal < endDateVal
    );
  }

  setTableSearchSetting(tableId: string, search: string) {
    const key = 'session-table-settings';
    let settings: Record<string, { search: string }> = {};
    // Read existing settings
    const existing = sessionStorage.getItem(key);
    if (existing) {
      try {
        settings = JSON.parse(existing);
      } catch {
        settings = {};
      }
    }
    // Update/add current table's search
    settings[tableId] = { search }; // for future we can add different settings within this object

    // Save back to sessionStorage
    sessionStorage.setItem(key, JSON.stringify(settings));
  }

  getTableSearchSetting(tableId: string): string {
    const key = 'session-table-settings';
    const existing = sessionStorage.getItem(key);
    if (existing) {
      try {
        const settings = JSON.parse(existing);
        return settings[tableId]?.search || '';
      } catch {
        return '';
      }
    }
    return '';
  }

  findDifferences<T extends object>(obj1: T, obj2: T): Partial<T> | undefined {
    const diff: Partial<T> = {};

    // Get all keys from both objects to ensure we cover everything that might be different or new in obj2
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    for (const key of allKeys) {
      const val1 = (obj1 as any)[key];
      const val2 = (obj2 as any)[key];

      // 1. Check for missing properties in obj2 (these aren't included in the 'diff' object by this implementation's logic, focusing only on new/changed in obj2)
      if (!(key in obj2)) {
        continue;
      }

      // 2. Check for simple value difference (including null/undefined differences)
      if (val1 !== val2) {
        // If both are objects (and not null/arrays), recurse
        if (this.isObject(val1) && this.isObject(val2) && !Array.isArray(val1) && !Array.isArray(val2)) {
          const subDiff = this.findDifferences(val1, val2);
          if (subDiff && Object.keys(subDiff).length > 0) {
            (diff as any)[key] = subDiff;
          }
        } else {
          // For primitives, arrays, or type mismatches, the value from obj2 is the difference
          (diff as any)[key] = val2;
        }
      }
    }

    // Return the diff object if it has any keys, otherwise return undefined
    return Object.keys(diff).length > 0 ? diff : undefined;
  }

  isObject(item: any): boolean {
    return (item && typeof item === 'object' && !Array.isArray(item) && item !== null);
  }

  areArraysEqual(arr1: any[] | undefined | null, arr2: any[] | undefined | null): boolean {
    if (arr1 === arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    return true;
  }

  patchObject<T extends object>(targetObject: T, patchObject: Partial<T>): void {
    if (!targetObject || !patchObject) {
      return;
    }

    // Iterate over the keys present in the patch object
    const keys = Object.keys(patchObject) as (keyof T)[];

    for (const key of keys) {
      const currentValue = targetObject[key];
      const patchValue = patchObject[key];

      // If the values are strictly equal (e.g., both undefined, both null, or same primitive value), skip
      if (currentValue === patchValue) {
        continue;
      }

      // Handle complex types (plain objects) using recursion
      if (this.isObject(currentValue) && this.isObject(patchValue)) {
        // Recurse into the nested objects
        this.patchObject(currentValue as object, patchValue as Partial<object>);
      }
      // Handle arrays
      else if (Array.isArray(currentValue) && Array.isArray(patchValue)) {
        // If arrays are different (shallow check), replace the entire array reference
        if (!this.areArraysEqual(currentValue, patchValue)) {
          (targetObject as any)[key] = patchValue;
        }
      }
      // Handle primitives and full object/array replacements where types might differ
      else {
        // Apply the patch value directly to the target object
        (targetObject as any)[key] = patchValue;
      }
    }
  }
}
